import { FormInput } from "@components/FormInput";
import { FormMultiSelect } from "@components/FormMultiSelect";
import { FormSelect } from "@components/FormSelect";
import { Container } from "@components/crud/Container";
import { Form } from "@components/crud/Form";
import { Loader } from "@components/crud/Loader";
import { Toolbar } from "@components/crud/Toolbar";
import { useApiSelectOptions } from "@hooks/useApiSelectOptions";
import Grid from "@mui/material/Unstable_Grid2";
import {
  ModelGameConcept,
  ModelMedia,
  useAdminGameConceptConceptIdDelete,
  useAdminGameConceptConceptIdGet,
  useAdminPositionGet,
  useAdminSkillGet,
  useAdminSportGet
} from "@sportsgravyengineering/sg-api-react-sdk";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Typography, FormLabel } from "@mui/material";
import { styled } from "@mui/material/styles";
import { organizationAtom } from "@recoil/auth";
import { useRecoilState } from "recoil";
import { useSnackbar } from "notistack";
import { ConfirmationDialog } from "@components/ConfirmationDialog";
import { hasPermission } from "@services/Casbin";
import ReactPlayer from "react-player";

const StyledFormLabel = styled(FormLabel)(({ theme }) => ({
  marginBottom: "0.25rem",

  "& .MuiFormLabel-asterisk": {
    color: theme.palette.error.main
  }
}));

const StyledGrid = styled(Grid)(() => ({
  "& video": {
    objectFit: "cover"
  }
}));

export const GameConceptView = () => {
  const navigate = useNavigate();
  const { conceptId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [toDelete, setToDelete] = useState<ModelGameConcept | null>(null);
  const [permissions, setPermissions] = useState({
    delete: false,
    edit: false
  });

  const [positions] = useState("");
  const [skills] = useState("");
  const [files, setFiles] = useState<ModelMedia[]>([]);
  const [organizationId] = useRecoilState(organizationAtom);

  const {
    data: gameConcept,
    isFetching: isGameConceptFetching,
    error: error
  } = useAdminGameConceptConceptIdGet(conceptId as string);
  useEffect(() => {
    if (error?.code == "ERR_BAD_REQUEST") navigate("/not-found");
  }, [error]);
  const { data: sports, isLoading: isSportLoading } = useAdminSportGet({
    organizationId: organizationId!
  });
  const sportOptions = useMemo(
    () =>
      sports?.data?.map((sport) => ({
        label: sport.name!,
        value: sport.sportId!
      })) || [],
    [sports]
  );
  const { options: positionOptions, isLoading: positionOptionsLoading } =
    useApiSelectOptions({
      api: useAdminPositionGet,
      dataField: "positions",
      labelField: "name",
      valueField: "positionId",
      params: {
        sportId: positions || gameConcept?.data?.sportId
      },
      options: {
        query: {
          enabled: !!positions || !!gameConcept?.data?.sportId
        }
      }
    });

  const { options: skillOptions, isLoading: skillOptionsLoading } =
    useApiSelectOptions({
      api: useAdminSkillGet,
      dataField: "skills",
      labelField: "name",
      valueField: "skillId",
      params: {
        sportId: skills || gameConcept?.data?.sportId
      },
      options: {
        query: {
          enabled: !!skills || !!gameConcept?.data?.sportId
        }
      }
    });

  const { control, reset } = useForm();

  useEffect(() => {
    if (!isGameConceptFetching && gameConcept?.data) {
      reset(
        {
          name: gameConcept?.data?.name,
          description: gameConcept?.data?.description,
          sportId: gameConcept?.data?.sportId,
          positions: gameConcept?.data?.positions?.map(
            (position) => position.positionId
          ),
          skills: gameConcept?.data?.skills?.map((skill) => skill.skillId)
        },
        {
          keepDirtyValues: true
        }
      );
      setFiles(gameConcept?.data?.media || []);
    }
  }, [gameConcept]);

  const { mutateAsync: deleteAsync, isLoading: isDeleting } =
    useAdminGameConceptConceptIdDelete();

  const onConfirmDelete = async () => {
    if (!toDelete?.conceptId) return;
    try {
      await deleteAsync({ conceptId: toDelete.conceptId });
      enqueueSnackbar("Game concept deleted successfully", {
        variant: "success"
      });
      setToDelete(null);
      navigate("/game-concepts");
    } catch (error) {
      enqueueSnackbar(
        "Something went wrong! Unable to delete the game concept.",
        {
          variant: "error"
        }
      );
      setToDelete(null);
    }
  };

  useEffect(() => {
    const checkPermission = async (permissionId, permission) => {
      const res = await hasPermission(
        "ORGANIZATION",
        organizationId!,
        permissionId as string,
        permission as string
      );
      return res;
    };
    const fetchPermissions = async () => {
      const del = await checkPermission("admin.game-concepts", "DELETE");
      const edit = await checkPermission("admin.game-concepts", "EDIT");
      setPermissions({
        delete: del,
        edit
      });
    };
    fetchPermissions();
  }, []);
  return (
    <Container>
      <Toolbar
        title="View Game Concept"
        backBtnClick={() => navigate("/game-concepts")}
        {...(permissions.edit && {
          editBtnClick: () => navigate(`/game-concepts/${conceptId}/edit`)
        })}
        {...(permissions.delete && {
          deleteBtnClick: () => setToDelete(gameConcept!.data)
        })}
      />
      <Form>
        <Loader
          isLoading={
            isSportLoading ||
            isGameConceptFetching ||
            positionOptionsLoading ||
            skillOptionsLoading
          }
        >
          <Grid container spacing={3}>
            <Grid data-testid="gameConcept-name" xs={12} md={6}>
              <FormInput
                control={control}
                name="name"
                type="text"
                label="Name"
                required={true}
                disabled={true}
                rules={{
                  required: "Name is required"
                }}
              />
            </Grid>
            <Grid data-testid="gameConcept-sport" xs={12} md={6}>
              <FormSelect
                control={control}
                name="sportId"
                label="Sport"
                required={true}
                options={sportOptions}
                disabled={true}
                rules={{
                  required: "Sport is required"
                }}
              />
            </Grid>
            <Grid data-testid="gameConcept-position" xs={12} md={6}>
              <FormMultiSelect
                control={control}
                name="positions"
                label="Positions"
                value="positions"
                required={false}
                disabled={true}
                options={positionOptions}
              />
            </Grid>
            <Grid data-testid="gameConcept-skill" xs={12} md={6}>
              <FormMultiSelect
                control={control}
                name="skills"
                label="Skills"
                value="skills"
                required={false}
                disabled={true}
                options={skillOptions}
              />
            </Grid>
            <Grid data-testid="gameConcept-description" xs={12} md={12}>
              <FormInput
                control={control}
                name="description"
                type="text"
                label="Description"
                multiline={true}
                disabled={true}
                rules={{
                  required: "Description is required"
                }}
              />
            </Grid>
            {files.length > 0 && (
              <Grid xs={12} md={12}>
                <StyledFormLabel>
                  <Typography display="inline" variant="formLabel">
                    {"Photo/Video Content"}
                  </Typography>
                </StyledFormLabel>
              </Grid>
            )}
            {files.map((item: ModelMedia) => {
              return (
                <StyledGrid
                  xs={12}
                  md={2}
                  key={Math.random().toString()}
                  data-testid="gameConcept-media"
                >
                  {item?.mimeType?.startsWith("image") ? (
                    <img
                      src={`${item.baseUrl}${item?.path}`}
                      style={{
                        width: "190px",
                        height: "190px"
                      }}
                    />
                  ) : (
                    <ReactPlayer
                      playing={true}
                      height={"190px"}
                      width={"190px"}
                      url={`${item?.baseUrl}${item?.path}`}
                      controls
                      style={{
                        objectFit: "cover",
                        width: "100%",
                        height: "100%"
                      }}
                    />
                  )}
                </StyledGrid>
              );
            })}
          </Grid>
        </Loader>
      </Form>
      <ConfirmationDialog
        open={!!toDelete}
        title="Delete Game Concept?"
        body={`Are you sure you want to delete ${toDelete?.name}?`}
        close={() => setToDelete(null)}
        onConfirm={onConfirmDelete}
        onCancel={() => setToDelete(null)}
        isConfirming={isDeleting}
        confirmBtnVariant="admin-warning"
        icon="warning"
      />
    </Container>
  );
};
